import "katex/dist/katex.min.css"

import { Typography } from "@suraasa/placebo-ui"
import ReactMarkdown from "react-markdown"
import rehypeKatex from "rehype-katex"
import remarkGfm from "remark-gfm"
import remarkMath from "remark-math"

type Props = {
  children?: string
  showTitle?: boolean
  isLoading?: boolean
}

const TOKEN_REGEX =
  /\[\[__STOP__\]\]|\[\[__ERROR__\]\]|\[\[__BREAK__\]\]|\[\[__END__\]\]|\{'status':\s*'success'\}|\{'status':\s*'failed'\}/g

const TITLE_REGEX = /#+\s*[Tt]itle\s*\n([^\n]+)/

const MarkdownRenderer = ({ children, showTitle = false }: Props) => {
  if (!children) return null

  let data = children

  data = data.replace(TOKEN_REGEX, "")

  if (!showTitle) {
    data = data.replace(TITLE_REGEX, "")
  }

  return (
    <ReactMarkdown
      remarkPlugins={[remarkMath, remarkGfm]}
      rehypePlugins={[rehypeKatex]}
      components={{
        a: ({ href, children }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-500 underline underline-offset-2 hover:opacity-80"
          >
            {children}
          </a>
        ),
        table: ({ children }) => (
          <table className="my-2 w-full border-collapse border border-onSurface-300">
            {children}
          </table>
        ),
        thead: ({ children }) => (
          <thead className="bg-onSurface-100">{children}</thead>
        ),
        tbody: ({ children }) => <tbody>{children}</tbody>,
        tr: ({ children }) => (
          <tr className="border-b border-onSurface-300">{children}</tr>
        ),
        th: ({ children }) => (
          <th className="border border-onSurface-300 px-2 py-1 text-left font-semibold">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className="border border-onSurface-300 px-2 py-1">{children}</td>
        ),
        h1: ({ children }) => (
          <Typography className="mt-4 text-title1">{children}</Typography>
        ),
        h2: ({ children }) => (
          <Typography className="mt-3 text-title2">{children}</Typography>
        ),
        h3: ({ children }) => (
          <Typography className="mb-0.5 mt-2 text-title3">
            {children}
          </Typography>
        ),
        h4: ({ children }) => (
          <Typography className="mt-1 text-title4">{children}</Typography>
        ),
        h5: ({ children }) => (
          <Typography className="mt-1 text-largeBody">{children}</Typography>
        ),
        h6: ({ children }) => (
          <Typography className="mt-1 text-strong">{children}</Typography>
        ),
        body: ({ children }) => (
          <Typography className="mt-1 text-body">{children}</Typography>
        ),
        strong: ({ children }) => (
          <Typography className="mt-1 inline text-strong">
            {children}
          </Typography>
        ),
        p: ({ children }) => (
          <Typography className="mt-0.5 text-body">{children}</Typography>
        ),
        li: ({ children }) => <li className="mt-1">{children}</li>,
        ul: ({ children }) => (
          <ul
            style={{
              paddingInlineStart: "20px",
              listStyleType: "disc",
            }}
            className="mt-1.5"
          >
            {children}
          </ul>
        ),
        ol: ({ children }) => (
          <ol
            style={{
              paddingInlineStart: "20px",
              listStyleType: "decimal",
            }}
            className="mt-1.5"
          >
            {children}
          </ol>
        ),
      }}
    >
      {data}
    </ReactMarkdown>
  )
}

export default MarkdownRenderer
